import React, { type FC } from "react";

export type StatusType =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "online"
  | "offline"
  | "pending"
  | "in_progress"
  | "error"
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "free"
  | "starter"
  | "pro"
  | "enterprise"
  | (string & {});

interface StatusBadgeProps {
  status: StatusType;
  label?: string;
  className?: string;
  customIcon?: React.ReactNode;
  size?: "sm" | "md";
  withIcon?: boolean;
}

const getStatusConfig = (s: string) => {
  switch (s.toLowerCase()) {
    case "succeeded":
    case "success":
    case "online":
    case "completed":
    case "active": {
      return {
        icon: (
          <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
        ),
        styles:
          "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
      };
    }
    case "running":
    case "in_progress":
    case "progress": {
      return {
        icon: (
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
        ),
        styles:
          "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-900/80 dark:text-emerald-300 dark:border-slate-700",
      };
    }
    case "failed":
    case "error":
    case "invalid":
    case "offline":
    case "timed_out":
    case "canceled":
    case "cancelled":
    case "unpaid": {
      return {
        icon: (
          <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
        ),
        styles:
          "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20",
      };
    }
    case "queued":
    case "pending":
    case "renamed":
    case "partial":
    case "past_due":
    case "trialing":
    case "enterprise": {
      return {
        icon: (
          <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
        ),
        styles:
          "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
      };
    }
    case "added": {
      return {
        icon: (
          <svg
            className="h-2 w-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={4}
              d="M12 4v16m8-8H4"
            />
          </svg>
        ),
        styles:
          "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
      };
    }
    case "deleted": {
      return {
        icon: (
          <svg
            className="h-2 w-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={4}
              d="M20 12H4"
            />
          </svg>
        ),
        styles:
          "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20",
      };
    }
    case "starter":
    case "modified": {
      return {
        icon: (
          <svg
            className="h-2 w-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={4}
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        ),
        styles:
          "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20",
      };
    }
    case "pro": {
      return {
        icon: (
          <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
        ),
        styles:
          "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20",
      };
    }
    default: {
      return {
        icon: <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />,
        styles:
          "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20",
      };
    }
  }
};

export const StatusBadge: FC<StatusBadgeProps> = ({
  status,
  label,
  className = "",
  customIcon,
  size = "md",
  withIcon = true,
}) => {
  const config = getStatusConfig(status);
  const sizeClasses =
    size === "sm" ? "px-1.5 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-none border font-mono font-bold uppercase tracking-widest ${config.styles} ${sizeClasses} ${className}`}
    >
      {withIcon && (customIcon ?? config.icon)}
      <span>{label ?? status.replace("_", " ")}</span>
    </span>
  );
};
